import { useEffect, useState, useCallback } from "react";
import { Storefront } from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function FeaturedProductPicker({ token }) {
  const [products, setProducts] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prods, settings] = await Promise.all([
        fetch(`${API}/shop/products`).then((r) => r.json()),
        fetch(`${API}/shop/settings`).then((r) => r.json()),
      ]);
      setProducts(prods.products || []);
      setCurrent(settings.featured_product_id || null);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (pid) => {
    setSaving(true);
    setMsg("");
    try {
      const r = await fetch(`${API}/admin/shop/featured`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "X-Admin-Token": token },
        body: JSON.stringify({ product_id: pid }),
      });
      if (!r.ok) throw new Error("save failed");
      setCurrent(pid);
      setMsg(pid ? "Featured product pinned." : "Override cleared — auto-picks the highest-priced product now.");
    } catch {
      setMsg("Save failed. Check your token.");
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(""), 4000);
    }
  };

  return (
    <CollapsibleSection
      id="featured-drop"
      testId="admin-featured-product"
      title={<><Storefront size={18} weight="fill" className="text-[#FF1B91] inline mr-2" />Featured drop</>}
      subtitle="Pin any product as the hero on the Sacrifice page. Clear to auto-pick highest-priced."
      onRefresh={load}
      extraActions={msg && <span data-testid="featured-msg" className="text-[10px] uppercase tracking-[0.2em] text-[#FF1B91]">{msg}</span>}
    >
      {loading ? (
        <div className="text-white/50 text-sm">Loading products…</div>
      ) : products.length === 0 ? (
        <div className="border border-white/10 bg-[#0F0F0F] p-4 text-white/60 text-sm">No products in your Printful store yet.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <button
            type="button"
            disabled={saving}
            onClick={() => save(null)}
            data-testid="featured-clear"
            className={`border-2 p-4 text-left transition-all ${current == null ? "border-[#FF1B91] bg-[#FF1B91]/10" : "border-white/15 bg-[#0F0F0F] hover:border-white/40"}`}
          >
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/60 mb-2">Auto</p>
            <p className="text-white text-sm font-bold">Highest-priced (default)</p>
          </button>
          {products.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={saving}
              onClick={() => save(p.id)}
              data-testid={`featured-pick-${p.id}`}
              className={`border-2 overflow-hidden text-left transition-all ${Number(current) === Number(p.id) ? "border-[#FF1B91] ring-2 ring-[#FF1B91]" : "border-white/15 bg-[#0F0F0F] hover:border-white/40"}`}
            >
              <div className="aspect-square bg-[#141414]">
                {p.thumbnail_url && (
                  <img src={p.thumbnail_url} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                )}
              </div>
              <div className="p-3">
                <p className="text-white text-xs font-bold line-clamp-2 leading-snug">{p.name}</p>
                <p className="text-white/50 text-[11px] mt-1">${Math.round(p.price_from || 0)}{p.price_to && p.price_to !== p.price_from ? `–$${Math.round(p.price_to)}` : ""}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </CollapsibleSection>
  );
}
